import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { Box, Card, CardContent, Stack, TextField, Typography, MenuItem, Button } from '@mui/material';
import { SearchOutlined, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import backgroundPattern from '../../assets/pasbookpng.png';
import "./RecurrenceList.css";

const money = (value) => `₹${Number(value || 0).toFixed(2)}`;

function ReadOnlyForm({ data, onClose }) {
  const items = data?.items || [];
  const subtotal = Number(data?.subTotal || 0);
  const total = Number(data?.total || 0);
  const adjustment = Number(data?.adjustment || 0);
  const tax = total - subtotal - adjustment;

  return (
    <div className="recurring-view-modal" role="dialog" aria-modal="true" aria-label="View recurring invoice">
      <div className="recurring-view-header">
        <div className="recurring-view-title">
          <div className="recurring-view-icon">↻</div>
          <div>
            <h2>View Recurring Invoice</h2>
            <p>{data?.profileName || "Recurring invoice details"}</p>
          </div>
        </div>
        <button type="button" className="recurring-modal-close" onClick={onClose} aria-label="Close">×</button>
      </div>

      <div className="recurring-view-body">
        <section className="recurring-info-card">
          <div className="recurring-info-grid">
            <div className="recurring-info-field"><span>Customer Name</span><strong>{data?.customer || "-"}</strong></div>
            <div className="recurring-info-field"><span>Profile Name</span><strong>{data?.profileName || "-"}</strong></div>
            <div className="recurring-info-field"><span>Order Number</span><strong>{data?.orderNo || "-"}</strong></div>
            <div className="recurring-info-field"><span>Salesperson</span><strong>{data?.salesperson || "-"}</strong></div>
            <div className="recurring-info-field"><span>Repeat Every</span><strong>{data?.repeatEvery || "-"}</strong></div>
            <div className="recurring-info-field"><span>Payment Terms</span><strong>{data?.paymentTerms || "-"}</strong></div>
            <div className="recurring-info-field"><span>Start On</span><strong>{data?.startOn || "-"}</strong></div>
            <div className="recurring-info-field"><span>Ends On</span><strong>{data?.endsOn || "Never Expires"}</strong></div>
            <div className="recurring-info-field"><span>Project</span><strong>{data?.project || "-"}</strong></div>
            <div className="recurring-info-field"><span>Subject</span><strong>{data?.subject || "-"}</strong></div>
            <div className="recurring-info-field"><span>Status</span><strong><span className={`recurring-status ${String(data?.status || "Saved").toLowerCase()}`}>{data?.status || "Saved"}</span></strong></div>
          </div>
        </section>

        <section className="recurring-items-card">
          <div className="recurring-section-heading">
            <div><h3>Items &amp; Services</h3><span>{items.length} item{items.length === 1 ? "" : "s"}</span></div>
          </div>
          <div className="recurring-items-scroll">
            <table className="recurring-items-table">
              <thead>
                <tr><th>#</th><th>ITEM / DESCRIPTION</th><th>QTY</th><th>RATE</th><th>DISCOUNT</th><th>AMOUNT</th></tr>
              </thead>
              <tbody>
                {items.length ? items.map((it, index) => {
                  const qty = Number(it.qty || 0);
                  const rate = Number(it.rate || 0);
                  const discount = Number(it.discount || 0);
                  const amount = qty * rate * (1 - discount / 100);
                  return (
                    <tr key={it.id || index}>
                      <td className="row-number">{index + 1}</td>
                      <td className="item-name">{it.item || "-"}</td>
                      <td className="text-center">{qty}</td>
                      <td className="text-right">{money(rate)}</td>
                      <td className="text-center">{discount}%</td>
                      <td className="text-right amount-cell">{money(amount)}</td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan="6" className="recurring-empty-items">No items added.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <div className="recurring-view-bottom">
          <div className="recurring-notes-card">
            <h3>Customer Notes</h3>
            <p>{data?.customerNotes || "-"}</p>
            <h3 className="terms-heading">Terms &amp; Conditions</h3>
            <p>{data?.terms || "-"}</p>
          </div>
          <div className="recurring-summary-card">
            <div><span>Sub Total</span><strong>{money(subtotal)}</strong></div>
            <div><span>Tax</span><strong>{money(tax)}</strong></div>
            <div><span>Adjustment</span><strong>{money(adjustment)}</strong></div>
            <div className="summary-total"><span>Total</span><strong>{money(total)}</strong></div>
          </div>
        </div>
      </div>

      <div className="recurring-view-footer">
        <button type="button" className="rl-btn-secondary" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

export default function RecurrenceList() {
  const [notes, setNotes] = useState([]);
  const [viewData, setViewData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('All');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("recurringInvoices") || "[]");
      setNotes(Array.isArray(saved) ? saved : []);
    } catch {
      setNotes([]);
    }
  }, []);

  // Keep the recurring invoice preview modal independent from the app header/sidebar.
  // This also prevents the background page from scrolling while the preview is open.
  useEffect(() => {
    if (!viewData) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") setViewData(null);
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [viewData]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const confirmDelete = () => {
    const filtered = notes.filter((n) => n.id !== deleteId);
    localStorage.setItem("recurringInvoices", JSON.stringify(filtered));
    setNotes(filtered);
    setDeleteId(null);
    showToast("Deleted successfully", "success");
  };

  const totalValue = notes.reduce((sum, n) => sum + Number(n.total || 0), 0);
  const activeCount = notes.filter((n) => String(n.status || "Saved").toLowerCase() === "saved").length;
  const draftCount = notes.filter((n) => String(n.status || "").toLowerCase() === "draft").length;

  const STATUS_FILTERS = ['All', 'Saved', 'Draft'];

  const filteredNotes = useMemo(() => {
    const term = search.trim().toLowerCase();
    return notes.filter((note) => {
      const searchable = [note.customer, note.profileName, note.orderNo].filter(Boolean).join(' ').toLowerCase();
      const matchesSearch = !term || searchable.includes(term);
      const matchesStatus = filter === 'All' || String(note.status || '').toLowerCase() === filter.toLowerCase();
      return matchesSearch && matchesStatus;
    });
  }, [notes, search, filter]);

  return (
    <div className="rl-container">
      <div
        className="rl-background-watermark"
        style={{ backgroundImage: `url(${backgroundPattern})` }}
      />
      <div className="rl-header">
        <div className="rl-title-wrap">
          <div className="rl-page-icon">↻</div>
          <div>
            <h2>Recurring Invoices</h2>
            <p>Manage, track and review recurring invoices.</p>
          </div>
        </div>
        <button className="rl-btn-primary" onClick={() => navigate("/dashboard/recurring")}>＋ Create recurring invoice</button>
      </div>

      <div className="rl-stat-grid">
        <div className="rl-stat-card">
          <div className="rl-stat-icon">↻</div>
          <div><span>Total Recurring</span><strong>{notes.length}</strong><small>All recurring profiles</small></div>
        </div>
        <div className="rl-stat-card">
          <div className="rl-stat-icon">✓</div>
          <div><span>Active</span><strong>{activeCount}</strong><small>Saved recurring profiles</small></div>
        </div>
        <div className="rl-stat-card">
          <div className="rl-stat-icon">▣</div>
          <div><span>Draft</span><strong>{draftCount}</strong><small>Profiles in draft</small></div>
        </div>
        <div className="rl-stat-card">
          <div className="rl-stat-icon">₹</div>
          <div><span>Total Value</span><strong>{money(totalValue)}</strong><small>Recurring invoice value</small></div>
        </div>
      </div>

      {!filtersOpen ? (
        <button
          type="button"
          className="rl-show-filters-btn"
          onClick={() => setFiltersOpen(true)}
        >
          <SearchOutlined className="rl-show-filters-icon" />
          <span>Show Filters</span>
        </button>
      ) : (
        <Card className="rl-filter-card">
          <CardContent>
            <Box className="rl-filter-header">
              <button
                type="button"
                className="rl-filter-title-btn"
                onClick={() => setFiltersOpen(false)}
                aria-label="Hide filters"
                title="Hide filters"
              >
                <Box className="rl-filter-icon"><SearchOutlined /></Box>
                <Typography className="rl-filter-title">Filters</Typography>
                <ExpandLessIcon className="rl-filter-collapse-icon" />
              </button>
              <Button
                className="rl-filter-clear"
                onClick={() => { setSearch(''); setFilter('All'); }}
                disabled={!search && filter === 'All'}
              >
                Clear
              </Button>
            </Box>
            <Stack className="rl-filter-controls" direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                className="rl-filter-search"
                placeholder="Search customer, profile name or order number"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <SearchOutlined sx={{ color: '#94a3b8', mr: 1 }} /> }}
              />
              <TextField select className="rl-filter-status" value={filter} onChange={(e) => setFilter(e.target.value)}>
                {STATUS_FILTERS.map((item) => <MenuItem key={item} value={item}>{item === 'All' ? 'All statuses' : item}</MenuItem>)}
              </TextField>
              <Typography className="rl-filter-count">
                {filteredNotes.length} {filteredNotes.length === 1 ? 'record' : 'records'}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      )}

      <div className="rl-table-wrap">
        <div className="rl-table-heading">
          <div><h3>Recurring Invoice Profiles</h3><span>{filteredNotes.length} record{filteredNotes.length === 1 ? "" : "s"}</span></div>
        </div>
        <table className="rl-table">
          <thead>
            <tr><th>Customer</th><th>Profile Name</th><th>Order No</th><th>Repeat</th><th>Status</th><th>Total</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {filteredNotes.length === 0 && <tr><td colSpan="7" className="rl-empty"><div className="rl-empty-icon">↻</div><strong>No recurring invoices found</strong><span>{notes.length ? 'Try changing your search or status filter.' : 'Create your first recurring invoice to see it here.'}</span><button onClick={() => navigate("/dashboard/recurring")}>＋ Create recurring invoice</button></td></tr>}
            {filteredNotes.map((n) => (
              <tr key={n.id}>
                <td className="rl-customer">{n.customer || "-"}</td>
                <td>{n.profileName || "-"}</td>
                <td>{n.orderNo || "-"}</td>
                <td>{n.repeatEvery || "-"}</td>
                <td><span className={`recurring-status ${String(n.status || "Saved").toLowerCase()}`}>{n.status || "Saved"}</span></td>
                <td className="rl-total">{money(n.total)}</td>
                <td className="rl-actions">
                  <button className="rl-btn-sm view" onClick={() => setViewData(n)}>View</button>
                  <button className="rl-btn-sm edit" onClick={() => navigate("/dashboard/recurring", { state: { editData: n } })}>Edit</button>
                  <button className="rl-btn-sm delete" onClick={() => setDeleteId(n.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {viewData && createPortal(
        <div className="rl-modal-overlay" onClick={() => setViewData(null)}>
          <div className="rl-modal recurring-view-modal-shell" onClick={(e) => e.stopPropagation()}>
            <ReadOnlyForm data={viewData} onClose={() => setViewData(null)} />
          </div>
        </div>,
        document.body
      )}

      {deleteId !== null && (
        <div className="rl-modal-overlay">
          <div className="rl-confirm-box">
            <div className="rl-confirm-icon">🗑️</div>
            <h3>Delete Recurring Invoice?</h3>
            <p>This action cannot be undone.</p>
            <div className="rl-confirm-actions">
              <button className="rl-btn-secondary" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="rl-btn-danger" onClick={confirmDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`rl-toast ${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}
